import { MatchStatus, MatchUnitSize } from '@uefadigital/panenka';
import { PkBox, PkButton, PkIcon, PkIdentifier } from '@uefadigital/panenka-react';
import useViewportSpy from 'beautiful-react-hooks/useViewportSpy';
import clsx from 'clsx';
import type { CSSProperties } from 'react';
import { useRef, useMemo, memo } from 'react';

import { getRoundLabel } from '../../../matches-calendar/utils/labels';
import useLivescoresBroadcasters from '../../hooks/broadcasters';
import { useLivescoresMatches } from '../../hooks/matches';
import { useLivescoresOptions } from '../../store';
import type { LivescoreTab } from '../../store/types';
import { getCompCode } from '../../utils/compCode';
import { getCompName } from '../../utils/compName';

import { useCompetitionLinkRule } from '@hooks/useLinkRule';
import { useWindowSize } from '@hooks/useWindowSize';
import type { BroadcastersParams } from '@queries/broadcasters';
import { hydrateWithTranslations } from '@services/TranslatorService';
import Image from '@shared/Image';
import LiveIndicator from '@shared/LiveIndicator';
import MatchUnit from '@shared/MatchUnit';
import Broadcasters from '@shared/MatchUnit/Broadcasters';
import { useTranslateFn } from '@shared/Translate';
import Windowizer from '@shared/Windowizer';
import type { Match } from '@shared-types/match.types';
import { groupBy, orderBy } from '@utilities/collection-utils';
import { dateToString } from '@utilities/dateTime';
import { isFriendly, matchLineupsAvailable } from '@utilities/live';
import { statusSorting } from '@utilities/match-sorting';
import { imgBaseUrl } from '@utilities/WindowContext';

import * as classes from './styles.module.scss';

const matchIsLive = (match: Match) =>
  (match.status === MatchStatus.UPCOMING && matchLineupsAvailable(match)) || match.status === MatchStatus.LIVE;

const MatchesPlaceholder = ({ maxColumns, numberOfMatches }: { maxColumns: number; numberOfMatches: number }) => {
  const { isDesktop } = useWindowSize();
  const fakeMatches = useMemo(() => Array.from({ length: numberOfMatches }, (_, i) => ({ id: i })), [numberOfMatches]);

  return (
    <div
      className={clsx(
        classes.competitionBox,
        'pk-d--flex pk-flex--column pk-gap--m pk-gap-md--l pk-p--m pk-p-md--l pk-bg--elevation-01 pk-rounded--l',
        !isDesktop && 'pk-border',
      )}
    >
      <div className='pk-visibility--invisible' style={{ height: isDesktop ? 48 : 32 }}>
        &nbsp;
      </div>
      <div className={classes.grid} style={{ '--max-columns': maxColumns } as CSSProperties}>
        {fakeMatches.map((m) => (
          <div className={classes.sklMatchGroup} key={`fakeMatch_${m.id}`} />
        ))}
      </div>
    </div>
  );
};

const Header = ({ matches, hasLiveMatches, date }: { matches: Match[]; hasLiveMatches: boolean; date: string }) => {
  const t = useTranslateFn();
  const { isDesktop } = useWindowSize();
  const competition = useMemo(() => hydrateWithTranslations(matches?.at(0)?.competition), [matches]);
  const round = useMemo(() => matches?.at(0)?.round, [matches]);
  const seasonYear = useMemo(() => matches?.at(0)?.seasonYear, [matches]);
  const roundLabel = getRoundLabel(matches, t);
  const isFriendlies = useMemo(() => matches?.every(isFriendly), [matches]);
  const iconSize = isDesktop ? 48 : 32;

  const url = useCompetitionLinkRule(
    competition.id,
    round?.phase,
    `fixtures-results/#/d/${dateToString(date, 'YYYY-MM-DD')}`,
  );

  const compCode = useMemo(() => getCompCode(competition, round?.phase), [competition, round?.phase]);
  const compName = useMemo(() => getCompName(competition, seasonYear), [competition, seasonYear]);
  const logo = `${imgBaseUrl}/imgml/uefacom/elements/logos/competitions/light/${compCode}` + '.svg';

  return (
    <div className='pk-d--flex pk-align-items--start pk-justify-content--between pk-gap--m'>
      <div className='pk-d--flex pk-align-items--center pk-justify-content--start pk-gap--m pk-gap-md--l pk-overflow--hidden'>
        <PkIdentifier className={classes.headerIdentifier}>
          <Image slot='prefix' src={logo} width={iconSize} height={iconSize} alt={compName} />
          <span slot='primary' className='pk-font-size-md--l'>
            {compName}
          </span>
          {!isFriendlies && (
            <span slot='secondary' className='pk-font-size-md--m'>
              {roundLabel}
            </span>
          )}
        </PkIdentifier>
        {hasLiveMatches && <LiveIndicator label='live' />}
      </div>

      <PkButton appearance='text' linkHref={url} linkTarget='_blank' className='pk-flex--shrink-0'>
        <span className='pk-font-size--s pk-font-size-md--m pk-line-height--lg'>
          {t(isDesktop ? 'go_to_the_competition' : 'see_all')}
        </span>
        {isDesktop && <PkIcon name='navigation-chevron-right' />}
      </PkButton>
    </div>
  );
};

const CompMatches = ({
  matches,
  updateMatch,
  isVisible,
  date,
}: {
  matches: Match[];
  updateMatch: (match: Match) => void;
  isVisible: boolean;
  date: string;
}) => {
  const { options } = useLivescoresOptions();
  const { isDesktop } = useWindowSize();

  const match = useMemo(() => matches?.at(0), [matches]);
  const competition = useMemo(() => hydrateWithTranslations(match?.competition), [match]);
  const round = useMemo(() => match?.round, [match]);
  const matchDay = useMemo(() => match?.matchday, [match]);
  const matchDate = useMemo(() => match && dateToString(match.kickOffTime.dateTime ?? match.kickOffTime.date), [match]);

  const loadBroadcasters = useMemo(
    () => isVisible && matches?.some((match) => match.status === MatchStatus.UPCOMING && match.kickOffTime),
    [isVisible, matches],
  );
  const broadcasterOptions = useMemo<BroadcastersParams>(
    () => ({
      competitionId: competition?.id,
      roundId: round?.id,
      matchDayId: matchDay?.id,
      startDate: matchDate,
      endDate: matchDate,
    }),
    [competition?.id, round?.id, matchDay?.id, matchDate],
  );
  const broadcasters = useLivescoresBroadcasters(broadcasterOptions, loadBroadcasters);

  const muSize = useMemo(() => (isDesktop ? MatchUnitSize.L_PLUS : MatchUnitSize.S_PLUS), [isDesktop]);

  const hasLiveMatches = useMemo(() => matches.some(matchIsLive), [matches]);
  const topModule = useMemo(
    () =>
      hasLiveMatches ||
      (matches.some((match) => [MatchStatus.FINISHED, MatchStatus.ABANDONED].includes(match.status)) &&
        matches.some((match) => match.status === MatchStatus.UPCOMING)),
    [hasLiveMatches, matches],
  );

  const sortedMatches = useMemo(() => matches.toSorted(statusSorting), [matches]);

  return (
    <PkBox
      className={clsx(classes.competitionBox, topModule && classes.competitionBoxTop)}
      stroke={!isDesktop}
      backgroundColor='elevation-01'
    >
      <div className='pk-d--flex pk-flex--column pk-gap--m pk-gap-md--l'>
        <Header matches={matches} hasLiveMatches={hasLiveMatches} date={date} />
        <div className={classes.grid} style={{ '--max-columns': options.maxColumns } as CSSProperties}>
          {sortedMatches.map((match) => (
            <Windowizer key={match.id} hiddenClassName={classes.sklMatchGroup} hideOnExit>
              <MatchUnit
                className={classes.matchUnit}
                options={options}
                match={match}
                size={muSize}
                onUpdate={updateMatch}
                hideContext={isFriendly(match) || options.hideContext}
              />
            </Windowizer>
          ))}
        </div>
        {!!broadcasters?.length && <Broadcasters broadcasters={broadcasters} showLabel className='pk-pt-md--0' />}
      </div>
    </PkBox>
  );
};

const intersectionOptions: IntersectionObserverInit = { rootMargin: '50% 0px' };
const MatchesTab = ({ tab }: { tab: LivescoreTab }) => {
  const { options } = useLivescoresOptions();

  const spyRef = useRef<HTMLDivElement>(null);
  const isVisible = useViewportSpy(spyRef, intersectionOptions);
  const wasVisible = useRef(isVisible);
  wasVisible.current ||= isVisible;
  const { matches, updateMatch } = useLivescoresMatches(wasVisible.current ? tab : null);

  const matchesByRoundAndType = useMemo(
    () =>
      orderBy(
        Object.entries(
          groupBy(matches, (m) => `${m.competition?.id}_${m.round?.id}-${m.type}_${matchIsLive(m) ? 0 : 1}`),
        ),
        ([k]) => k.split('_').at(-1),
      ),
    [matches],
  );

  return (
    <div ref={spyRef} className={classes.group}>
      <div className={classes.date}>{dateToString(tab.date, 'dddd D MMMM YYYY')}</div>
      {matches?.length ? (
        matchesByRoundAndType.map(([roundLeg, roundMatches]) => (
          <CompMatches
            key={roundLeg}
            matches={roundMatches}
            updateMatch={updateMatch}
            isVisible={isVisible}
            date={tab.date}
          />
        ))
      ) : (
        <MatchesPlaceholder maxColumns={options.maxColumns} numberOfMatches={tab.numberOfMatches} />
      )}
    </div>
  );
};

export default memo(MatchesTab);
